import { EventIdentifier, FeatureTeamEvent, TrackingEventType, useAnalytics, useIntl } from '@capsicum/common';
import { Alert, CountrySelectInput, ModalService, SkeletonItem } from '@capsicum/common-web';
import { ChevronRightIcon, InfoIcon } from '@capsicum/common-web/icons';
import {
	type CountryRegisterStepProps,
	countryRegistrationStepAtom,
	type RegisterSchemaType,
	useCountryStepValidation
} from '@capsicum/socios-core';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { usePreventUSModal } from '../../../hooks';
import { StepperPage } from '../StepperPage';
import { TermsAndConditions } from '../TermsAndConditions';

export const CountryStep = ({ onValidityChange, usContactUSLink }: CountryRegisterStepProps) => {
	const { $t } = useIntl();
	const analytics = useAnalytics();

	useCountryStepValidation({ onValidityChange });
	const form = useFormContext<RegisterSchemaType>();
	const { showPreventUSModal } = usePreventUSModal(usContactUSLink);
	const { isUserCountriesQueryLoading } = useAtomValue(countryRegistrationStepAtom);
	const country = form.watch('country');

	useEffect(() => {
		if (country?.iso3 !== 'USA' || !usContactUSLink) return;

		const modalID = showPreventUSModal();

		return () => {
			ModalService.removeModal(modalID);
		};
	}, [country?.iso3, showPreventUSModal, usContactUSLink]);

	useEffect(
		() =>
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.PageView, {
				feature_team: FeatureTeamEvent.OFT,
				page: 'country_entry_page'
			}),
		[analytics]
	);

	useEffect(() => {
		analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
			extra_parameter: country.name,
			feature_team: FeatureTeamEvent.ORFT,
			page: 'country_entry_page',
			text: 'choose_your_country'
		});
	}, [country, analytics]);

	return (
		<StepperPage title={$t('auth.registration.countryStep.title')}>
			<S.Content>
				<S.FormFields>
					{isUserCountriesQueryLoading ? (
						<SkeletonItem height={56} width="100%" />
					) : (
						<CountrySelectInput
							autoCapitalize="none"
							Icon={S.StyledChevronRightIcon}
							label={$t('auth.registrationCountryOfResidence')}
							labelProp="name"
							name="country"
							searchPlaceholder={$t('auth.registrationCountryOfResidence')}
							testID="registration-form-country-input"
							variant="countryWithFlag"
						/>
					)}

					<TermsAndConditions />
				</S.FormFields>

				<S.AlertInfo
					closable={false}
					icon={InfoIcon}
					subTitle={$t('auth.registration.countryStep.instructionalMessage')}
					testID="registration-country-info-alert"
					type="message"
				/>
			</S.Content>
		</StepperPage>
	);
};

namespace S {
	export const Content = styled.div`
		height: 100%;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	`;

	export const StyledChevronRightIcon = styled(ChevronRightIcon)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const FormFields = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.large};
	`;

	export const AlertInfo = styled(Alert)`
		margin-top: ${({ theme }) => theme.spacings.large};
	`;
}
